import { FIXED_LIMITS, Permissions, serializeBitfield, toBitfield, type PermissionName, type RoleDto } from '@lagerfeuer/shared';
import { ArrowDown, ArrowUp, GripVertical, Plus, Search, ShieldAlert, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ImageUpload } from '../../components/ImageUpload';
import { ConfirmDialog } from '../../components/Modal';
import { EmojiField } from '../../components/EmojiField';
import { EmojiText } from '../../components/EmojiText';
import { SaveBar, SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { rolesApi, type RoleInput } from '../../lib/endpoints';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { canEditRole, grantable, roleColor, useActor, type Actor } from './hierarchy';
import { PERMISSION_GROUPS } from './permissionCatalog';
import { neighbourIndex, reorderRoles } from './roleOrder';
import { useEdits } from './useEdits';

const SWATCHES = [0xff7a18, 0xffb347, 0xe2452b, 0xc2185b, 0x9c6ade, 0x4f8fdb, 0x2bb3a3, 0x7bc67e, 0xd4a017, 0x8d6e63, 0x90a4ae];

const dispatch = useAppStore.getState().dispatch;

/** Tab „Rollen": Liste (Drag & Drop + Tastatur), anlegen, Editor für Anzeige und Rechte. */
export function RolesTab({ serverId }: { serverId: string }) {
  const roles = useAppStore((s) => s.roles[serverId]);
  const actor = useActor(serverId);
  const [selected, setSelected] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const ordered = useMemo(() => (roles ?? []).filter((r) => !r.isEveryone).sort((a, b) => b.position - a.position), [roles]);
  const everyone = roles?.find((r) => r.isEveryone);
  const editable = useMemo(() => new Set(actor ? ordered.filter((r) => canEditRole(actor, r)).map((r) => r.id) : []), [ordered, actor]);
  if (!actor || !roles) return null;
  const current = roles.find((r) => r.id === selected) ?? null;

  const move = async (id: string, toIndex: number) => {
    const changes = reorderRoles(ordered, editable, id, toIndex);
    if (changes.length === 0) return;
    const before = ordered.map((r) => ({ id: r.id, position: r.position }));
    dispatch('ROLE_POSITIONS_UPDATE', { serverId, positions: changes });
    try {
      const res = await rolesApi.positions(serverId, changes);
      if (Array.isArray(res)) dispatch('ROLE_POSITIONS_UPDATE', { serverId, positions: res.map((r) => ({ id: r.id, position: r.position })) });
    } catch (e) {
      dispatch('ROLE_POSITIONS_UPDATE', { serverId, positions: before });
      toast.error('Sortieren fehlgeschlagen', errorMessage(e));
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const role = await rolesApi.create(serverId, { name: 'Neue Rolle' });
      dispatch('ROLE_CREATE', role);
      setSelected(role.id);
    } catch (e) {
      toast.error('Rolle konnte nicht erstellt werden', errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Rollen">
        Rollen bündeln Rechte und färben Namen. Höhere Rollen stehen oben. Du kannst nur Rollen unterhalb deiner höchsten Rolle bearbeiten und nur Rechte vergeben, die du selbst
        hast.
      </SettingsHeader>
      <div className={`roles-layout ${current ? 'has-selection' : ''}`}>
        <div className="roles-list-col">
          <button className="btn btn-primary btn-block" onClick={() => void create()} disabled={creating} data-testid="role-create">
            <Plus size={16} /> Rolle erstellen
          </button>
          <p className="field-hint" id="roles-dnd-hint">
            Ziehen zum Sortieren – oder Rolle fokussieren und Alt + Pfeil hoch/runter drücken.
          </p>
          <ol className="roles-list" aria-describedby="roles-dnd-hint" data-testid="role-list">
            {ordered.map((r, i) => {
              const canMove = editable.has(r.id);
              return (
                <li
                  key={r.id}
                  className={`role-item ${selected === r.id ? 'active' : ''} ${overIdx === i && dragId ? 'drop-target' : ''}`}
                  draggable={canMove}
                  onDragStart={(e) => {
                    setDragId(r.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', r.id);
                  }}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverIdx(null);
                  }}
                  onDragOver={(e) => {
                    if (!dragId || !editable.has(r.id)) return;
                    e.preventDefault();
                    setOverIdx(i);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId) void move(dragId, i);
                    setDragId(null);
                    setOverIdx(null);
                  }}
                  data-testid="role-item"
                  data-role-id={r.id}
                >
                  {canMove ? <GripVertical size={14} className="role-grip" aria-hidden="true" /> : <span className="role-grip-space" />}
                  <button
                    className="role-item-main"
                    onClick={() => setSelected(r.id)}
                    onKeyDown={(e) => {
                      if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                      e.preventDefault();
                      const j = neighbourIndex(ordered, editable, r.id, e.key === 'ArrowUp' ? -1 : 1);
                      if (j !== null && canMove) void move(r.id, j);
                    }}
                    aria-current={selected === r.id ? 'true' : undefined}
                    data-testid="role-item-select"
                  >
                    <span className="role-dot" style={{ background: roleColor(r.color) ?? 'var(--lf-text-faint)' }} aria-hidden="true" />
                    {r.iconUrl ? <img src={r.iconUrl} alt="" className="role-icon" /> : r.unicodeEmoji ? <EmojiText value={r.unicodeEmoji} size={16} /> : null}
                    <span className="truncate">{r.name}</span>
                    {r.managed && <span className="tag">verwaltet</span>}
                  </button>
                  {canMove && (
                    <span className="role-move">
                      <button
                        className="icon-btn icon-btn-xs"
                        aria-label={`${r.name} nach oben`}
                        disabled={neighbourIndex(ordered, editable, r.id, -1) === null}
                        onClick={() => void move(r.id, i - 1)}
                        data-testid="role-move-up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        className="icon-btn icon-btn-xs"
                        aria-label={`${r.name} nach unten`}
                        disabled={neighbourIndex(ordered, editable, r.id, 1) === null}
                        onClick={() => void move(r.id, i + 1)}
                        data-testid="role-move-down"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
            {everyone && (
              <li className={`role-item everyone ${selected === everyone.id ? 'active' : ''}`} data-testid="role-item" data-role-id={everyone.id}>
                <span className="role-grip-space" />
                <button className="role-item-main" onClick={() => setSelected(everyone.id)} aria-current={selected === everyone.id ? 'true' : undefined} data-testid="role-item-select">
                  <span className="role-dot" aria-hidden="true" />
                  <span className="truncate">@everyone</span>
                </button>
              </li>
            )}
          </ol>
        </div>
        <div className="roles-editor-col">
          {current ? (
            <RoleEditor key={current.id} serverId={serverId} role={current} actor={actor} onBack={() => setSelected(null)} onDeleted={() => setSelected(null)} />
          ) : (
            <div className="empty" data-testid="role-editor-empty">
              Wähle eine Rolle, um sie zu bearbeiten.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EditableRole {
  name: string;
  color: number | null;
  hoist: boolean;
  mentionable: boolean;
  unicodeEmoji: string | null;
  permissions: string;
}

/** Editor einer Rolle: Anzeige + Rechte. Exportiert für Tests. */
export function RoleEditor({ serverId, role, actor, onBack, onDeleted }: { serverId: string; role: RoleDto; actor: Actor; onBack?: () => void; onDeleted?: () => void }) {
  const maxIcon = useAppStore((s) => s.limits.maxAvatarBytes);
  const base = useMemo<EditableRole>(
    () => ({ name: role.name, color: role.color, hoist: role.hoist, mentionable: role.mentionable, unicodeEmoji: role.unicodeEmoji, permissions: role.permissions }),
    [role],
  );
  const { value, patch, dirty, set, reset } = useEdits(base);
  const [view, setView] = useState<'anzeige' | 'rechte'>(role.isEveryone ? 'rechte' : 'anzeige');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const editable = canEditRole(actor, role);
  if (!value) return null;

  const save = async () => {
    setBusy(true);
    try {
      const body: RoleInput = role.isEveryone ? { permissions: patch.permissions } : { ...patch };
      if (body.name !== undefined) body.name = body.name.trim() || role.name;
      dispatch('ROLE_UPDATE', await rolesApi.update(serverId, role.id, body));
      reset();
      toast.success('Rolle gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await rolesApi.remove(serverId, role.id);
      dispatch('ROLE_DELETE', { serverId, roleId: role.id });
      onDeleted?.();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen', errorMessage(e));
      setBusy(false);
    }
    setConfirmDelete(false);
  };

  return (
    <div className="stack role-editor" data-testid="role-editor" data-role-id={role.id}>
      <div className="row">
        {onBack && (
          <button className="btn btn-ghost btn-sm roles-back" onClick={onBack} data-testid="role-editor-back">
            ← Rollen
          </button>
        )}
        <h2 className="settings-subheading truncate" style={{ margin: 0, flex: 1 }}>
          Rolle bearbeiten – {role.isEveryone ? '@everyone' : role.name}
        </h2>
        {!role.isEveryone && editable && (
          <button className="icon-btn" aria-label="Rolle löschen" onClick={() => setConfirmDelete(true)} data-testid="role-delete">
            <Trash2 size={18} />
          </button>
        )}
      </div>
      {!editable && (
        <p className="notice" role="note" data-testid="role-locked">
          <ShieldAlert size={16} aria-hidden="true" />
          {role.managed ? 'Diese Rolle wird von einer Integration verwaltet.' : 'Diese Rolle steht auf oder über deiner höchsten Rolle – du kannst sie nicht bearbeiten.'}
        </p>
      )}
      <div className="tabs" role="tablist">
        {!role.isEveryone && (
          <button role="tab" className="tab" aria-selected={view === 'anzeige'} onClick={() => setView('anzeige')} data-testid="role-tab-display">
            Anzeige
          </button>
        )}
        <button role="tab" className="tab" aria-selected={view === 'rechte'} onClick={() => setView('rechte')} data-testid="role-tab-permissions">
          Rechte
        </button>
      </div>

      {view === 'anzeige' && !role.isEveryone && (
        <div className="stack" role="tabpanel">
          <div className="field">
            <label htmlFor="role-name">Rollenname</label>
            <input id="role-name" className="input" value={value.name} maxLength={FIXED_LIMITS.roleNameMax} disabled={!editable} onChange={(e) => set('name', e.target.value)} data-testid="role-name" />
          </div>
          <div className="field">
            <span className="label">Farbe</span>
            <div className="swatches" role="radiogroup" aria-label="Rollenfarbe">
              <button
                role="radio"
                aria-checked={value.color === null}
                className="swatch swatch-none"
                aria-label="Keine Farbe"
                disabled={!editable}
                onClick={() => set('color', null)}
                data-testid="role-color-none"
              />
              {SWATCHES.map((c) => (
                <button
                  key={c}
                  role="radio"
                  aria-checked={value.color === c}
                  className="swatch"
                  style={{ background: roleColor(c)! }}
                  aria-label={`Farbe ${roleColor(c)}`}
                  disabled={!editable}
                  onClick={() => set('color', c)}
                  data-testid={`role-color-${c.toString(16)}`}
                />
              ))}
              <label className="swatch swatch-custom" title="Eigene Farbe">
                <span className="sr-only">Eigene Farbe</span>
                <input
                  type="color"
                  value={roleColor(value.color) ?? '#ff7a18'}
                  disabled={!editable}
                  onChange={(e) => set('color', parseInt(e.target.value.slice(1), 16))}
                  data-testid="role-color-custom"
                />
              </label>
            </div>
          </div>
          <label className="switch-row">
            <input type="checkbox" className="switch" checked={value.hoist} disabled={!editable} onChange={(e) => set('hoist', e.target.checked)} data-testid="role-hoist" />
            <span>Mitglieder dieser Rolle getrennt in der Mitgliederliste anzeigen</span>
          </label>
          <label className="switch-row">
            <input type="checkbox" className="switch" checked={value.mentionable} disabled={!editable} onChange={(e) => set('mentionable', e.target.checked)} data-testid="role-mentionable" />
            <span>Alle dürfen @{value.name || 'Rolle'} erwähnen</span>
          </label>
          <div className="field">
            <label htmlFor="role-emoji">Rollen-Emoji</label>
            <EmojiField id="role-emoji" value={value.unicodeEmoji} onChange={(v) => set('unicodeEmoji', v)} label="Rollen-Emoji" unicodeOnly disabled={!editable} testId="role-emoji" />
            <span className="field-hint">Wird neben dem Namen angezeigt (alternativ zum Icon).</span>
          </div>
          <ImageUpload
            label="Rollen-Icon"
            url={role.iconUrl}
            shape="square"
            maxBytes={maxIcon}
            disabled={!editable}
            onUpload={async (f) => dispatch('ROLE_UPDATE', await rolesApi.setIcon(serverId, role.id, f))}
            onRemove={async () => dispatch('ROLE_UPDATE', await rolesApi.removeIcon(serverId, role.id))}
            testId="role-icon-upload"
          />
        </div>
      )}

      {view === 'rechte' && (
        <PermissionToggles
          value={toBitfield(value.permissions)}
          grantable={grantable(actor)}
          disabled={!editable}
          onChange={(bits) => set('permissions', serializeBitfield(bits))}
        />
      )}

      <SaveBar dirty={dirty} busy={busy} onSave={() => void save()} onReset={reset} testId="role-save" />
      {confirmDelete && (
        <ConfirmDialog
          title={`Rolle „${role.name}" löschen?`}
          message="Alle Mitglieder verlieren diese Rolle. Das lässt sich nicht rückgängig machen."
          confirmLabel="Löschen"
          danger
          busy={busy}
          onConfirm={() => void remove()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

/** Gruppierte Rechte-Schalter einer Rolle. Nicht vergebbare Rechte (Hierarchie) sind deaktiviert. */
export function PermissionToggles({ value, grantable: allowedBits, disabled, onChange }: { value: bigint; grantable: bigint; disabled?: boolean; onChange: (v: bigint) => void }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  return (
    <div className="stack" role="tabpanel" data-testid="role-permissions">
      <div className="row">
        <div className="search-input">
          <Search size={16} aria-hidden="true" />
          <input className="input" placeholder="Rechte durchsuchen" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Rechte durchsuchen" data-testid="perm-search" />
        </div>
        <button className="btn btn-ghost btn-sm" disabled={disabled || value === 0n} onClick={() => onChange(value & ~allowedBits)} data-testid="perm-clear">
          Rechte leeren
        </button>
      </div>
      {PERMISSION_GROUPS.map((g) => {
        const items = g.items.filter((i) => !q || i.label.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
        if (items.length === 0) return null;
        return (
          <section key={g.id} className="perm-group" aria-labelledby={`perm-group-${g.id}`}>
            <h3 id={`perm-group-${g.id}`} className="label">
              {g.label}
            </h3>
            {items.map((i) => {
              const bit = Permissions[i.name];
              const on = (value & bit) === bit;
              const lockedByHierarchy = (allowedBits & bit) !== bit;
              return (
                <label key={i.name} className={`perm-row ${lockedByHierarchy ? 'locked' : ''}`} data-testid={`perm-row-${i.name}`}>
                  <span className="perm-text">
                    <span className="perm-label">{i.label}</span>
                    <span className="perm-desc">{i.description}</span>
                    {lockedByHierarchy && !disabled && <span className="perm-desc faint">Du hast dieses Recht selbst nicht und kannst es daher nicht vergeben.</span>}
                  </span>
                  <input
                    type="checkbox"
                    className="switch"
                    checked={on}
                    disabled={disabled || lockedByHierarchy}
                    onChange={(e) => onChange(e.target.checked ? value | bit : value & ~bit)}
                    data-testid={`perm-toggle-${i.name satisfies PermissionName}`}
                  />
                </label>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
